// An enum entry. Some enums carry extra keys (name, description, ...).
export interface EnumEntry {
  value: string | number;
  label: string;
}

type ConstantChar =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '_';

// ConstantName mirrors conformName at the type level: upper-cased, with
// anything outside [A-Z0-9_] replaced by `_`. Tail-recursive so long labels
// (school names) stay within the instantiation depth limit.
type ConstantName<
  S extends string,
  Acc extends string = '',
> = S extends `${infer Head}${infer Tail}`
  ? ConstantName<
      Tail,
      `${Acc}${Uppercase<Head> extends ConstantChar ? Uppercase<Head> : '_'}`
    >
  : Acc;

// EnumConstants maps each label's constant name to its value, so
// `InputType.MULTI_SELECT` types as `10`.
export type EnumConstants<V extends readonly EnumEntry[]> = {
  readonly [E in V[number] as ConstantName<E['label']>]: E['value'];
};

// EnumValue is the union of the values of an enum, so
// `EnumValue<typeof DataType>` types as `1 | 2 | ... | 7`.
export type EnumValue<E extends BaseEnum> = E['values'][number]['value'];

function conformName(name: string): string {
  return name.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
}

export default class BaseEnum<
  V extends readonly EnumEntry[] = readonly EnumEntry[],
> {
  // `const` keeps each value and label literal, including when called from a
  // JS enum file, which is what types the constants. Pass a plain array
  // literal: wrapping it in Object.freeze widens the types first.
  // Other keys are copied onto the enum, as EmberObject.create did (for
  // example the linced-app notification-transport-type `pushTransports`).
  static create<
    const V extends readonly EnumEntry[],
    X extends object = Record<never, never>,
  >({
    values,
    ...extra
  }: { values: V } & X): BaseEnum<V> & EnumConstants<V> & Omit<X, 'values'> {
    return Object.assign(new BaseEnum(values), extra) as BaseEnum<V> &
      EnumConstants<V> &
      Omit<X, 'values'>;
  }

  readonly values: V;

  constructor(values: V) {
    this.values = Object.freeze(values);
    // Set "constants" for all values.
    const constants = this as unknown as Record<string, EnumEntry['value']>;
    for (const val of values) {
      constants[conformName(val.label)] = val.value;
    }
  }

  getValue(label: string): V[number]['value'] | null {
    const obj = this.values.find((x) => x.label === label);
    return obj ? obj.value : null;
  }

  getLabel(value: unknown): string | null {
    const obj = this.values.find((x) => x.value === value);
    return obj ? obj.label : null;
  }

  getObject(value: unknown): V[number] | null {
    return this.values.find((x) => x.value === value) ?? null;
  }

  getAllValues(): V[number]['value'][] {
    return this.values.map((val) => val.value);
  }
}
